import React, { useState, useEffect } from "react"
import { Button } from "@mui/material"
import { makeStyles } from "makeStyles"
import {
	Answer as AnswerI,
	ProductAdvisorContext,
} from "@secureo/common/machines/productAdvisorMachine"
import searchClient from "@secureo/common/utils/algolia/searchClient"
import { getSearchStateFromSelectedAnswers } from "pages/tresorberater"
import { CountryCode } from "@secureo/common/typings/CountryCode"

const useStyles = makeStyles()(() => ({
	button: {
		border: "2px solid rgba(177, 191, 195, 0.3)",
		borderRadius: 10,
		padding: 20,
		transition: "0.3s",
		textTransform: "inherit",
		width: 180,
		height: 200,
		display: "block",
		background: "white",
		position: "relative",
		"&:hover": {
			border: "2px solid #009FB8",
		},
		"@media only screen and (max-width: 960px)": {
			display: "flex",
			textAlign: "left",
			width: "100%",
			height: "auto",
			marginBottom: 10,
			padding: 15,
		},
	},
	label: {
		width: "100%",
		height: 50,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		whiteSpace: "normal",
		wordWrap: "break-word",
		"@media only screen and (max-width: 960px)": {
			justifyContent: "flex-start",
		},
	},
	responsiveSVG: {
		width: "100%",
		height: 100,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		"@media only screen and (max-width: 960px)": {
			width: "200px",
		},
	},
}))

const Answer = ({
	selectedAnswers,
	questionId,
	answer,
	selectAnswer,
	setDescription,
	countryCode,
	categoryIdsBySlug,
}: Props) => {
	const [numberOfResults, setNumberOfResults] = useState(0)
	const { classes } = useStyles()
	const { label, icon: Icon, description } = answer

	const aggregatedSearchStateWithCurrentAnswer = getSearchStateFromSelectedAnswers(
		[...selectedAnswers, { questionId, answer }],
		countryCode,
		categoryIdsBySlug,
	)

	useEffect(() => {
		const lockFilter = aggregatedSearchStateWithCurrentAnswer?.menu?.lockType ?? "Alle Anzeigen"
		const facetFilters = Object.entries(
			aggregatedSearchStateWithCurrentAnswer.refinementList,
		).map(([attribute, refinements]) => {
			if (refinements.length === 1) return `${attribute}:${refinements[0]}`
			return refinements.map((refinement) => `${attribute}:${refinement}`)
		})
		const facetFiltersWithLockFilter = [...facetFilters, `lockType:${lockFilter}`]
		const numericFilters =
			Object.keys(aggregatedSearchStateWithCurrentAnswer?.range ?? {}).length > 0
				? Object.entries(aggregatedSearchStateWithCurrentAnswer?.range).map(
						([attribute, { min, max }]) => {
							return `${attribute}:${min} TO ${max}`
						},
				  )
				: null

		const additionalParams = numericFilters ? { numericFilters } : {}

		searchClient
			.search([
				{
					indexName: `TRE-de-${countryCode}-products`,
					params: {
						facetFilters: facetFiltersWithLockFilter as any,
						...additionalParams,
					},
				},
			])
			.then((results) => {
				const numberOfResults = results?.results?.[0]?.nbHits
				setNumberOfResults(numberOfResults)
			})
	}, [questionId, JSON.stringify(aggregatedSearchStateWithCurrentAnswer)])

	return (
		<div
			style={{ margin: 5 }}
			onMouseEnter={() => {
				setDescription(description)
			}}
		>
			<Button
				className={classes.button}
				onClick={() => selectAnswer(answer)}
				disabled={numberOfResults === 0}
			>
				<div className={classes.responsiveSVG}>{Icon && <Icon />}</div>
				<div className={classes.label}>
					<strong>
						{label} ({numberOfResults})
					</strong>
				</div>
			</Button>
		</div>
	)
}

interface Props {
	answer: AnswerI
	selectAnswer: (answer: AnswerI) => void
	setDescription: (description: string) => void
	countryCode: CountryCode
	categoryIdsBySlug: { [slug: string]: string }
	selectedAnswers: ProductAdvisorContext["selectedAnswers"]
	questionId: string
}

export default Answer
